<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AiMarkupAPI from 'dashboard/api/aiMarkup';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SectionCard from './components/SectionCard.vue';
import AutotagsTable from './components/AutotagsTable.vue';
import SpamRules from './components/SpamRules.vue';
import TestPanel from './components/TestPanel.vue';
import StatsPanel from './components/StatsPanel.vue';

const { t } = useI18n();

const state = ref('loading'); // loading | ready | error
const isSaving = ref(false);
const config = ref(null);
const stats = ref({});
const snapshot = ref('');

const normalize = raw => {
  const value = raw || {};
  return {
    ...value,
    enabled: value.enabled ?? true,
    provider: {
      base_url: '',
      model: '',
      temperature: 0.1,
      ...(value.provider || {}),
    },
    sentiment: {
      enabled: true,
      scope: 'first_message',
      instructions: '',
      create_labels: false,
      ...(value.sentiment || {}),
    },
    autotags: {
      enabled: true,
      max_tags: 3,
      items: [],
      ...(value.autotags || {}),
    },
    spam: { enabled: true, rules: [], ...(value.spam || {}) },
  };
};

const applyResponse = data => {
  config.value = normalize(data.config);
  stats.value = data.stats || {};
  snapshot.value = JSON.stringify(config.value);
};

const load = async () => {
  state.value = 'loading';
  try {
    const { data } = await AiMarkupAPI.show();
    applyResponse(data);
    state.value = 'ready';
  } catch (error) {
    state.value = 'error';
  }
};

const isDirty = computed(
  () => !!config.value && JSON.stringify(config.value) !== snapshot.value
);

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await AiMarkupAPI.updateConfig(config.value);
    applyResponse(data);
    useAlert(t('AI_MARKUP.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_MARKUP.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const reset = () => {
  config.value = JSON.parse(snapshot.value);
};

const scopeOptions = computed(() => [
  {
    value: 'first_message',
    label: t('AI_MARKUP.SENTIMENT.SCOPES.FIRST_MESSAGE'),
  },
  {
    value: 'all_incoming',
    label: t('AI_MARKUP.SENTIMENT.SCOPES.ALL_INCOMING'),
  },
]);

const maxTagsOptions = [1, 2, 3, 4, 5].map(value => ({
  value,
  label: String(value),
}));

const temperature = computed({
  get: () => String(config.value.provider.temperature ?? ''),
  set: value => {
    const number = parseFloat(String(value).replace(',', '.'));
    config.value.provider.temperature = Number.isNaN(number) ? 0 : number;
  },
});

onMounted(load);
</script>

<template>
  <SettingsLayout :is-loading="false" class="pb-10">
    <template #header>
      <BaseSettingsHeader
        :title="t('AI_MARKUP.HEADER.TITLE')"
        :description="t('AI_MARKUP.HEADER.DESCRIPTION')"
      >
        <template v-if="state === 'ready'" #actions>
          <span v-if="isDirty" class="text-xs text-n-amber-11">
            {{ t('AI_MARKUP.UNSAVED') }}
          </span>
          <Button
            v-if="isDirty"
            :label="t('AI_MARKUP.RESET')"
            size="sm"
            variant="ghost"
            color="slate"
            @click="reset"
          />
          <Button
            :label="t('AI_MARKUP.SAVE')"
            icon="i-lucide-check"
            size="sm"
            :is-loading="isSaving"
            :disabled="!isDirty || isSaving"
            @click="save"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="state === 'loading'"
        class="flex items-center justify-center gap-2 py-24 text-sm text-n-slate-11"
      >
        <Spinner class="text-n-brand" />
        {{ t('AI_MARKUP.LOADING') }}
      </div>
      <div
        v-else-if="state === 'error'"
        class="flex flex-col items-center gap-3 px-6 py-16 mt-4 text-center border border-dashed rounded-xl border-n-weak"
      >
        <span class="grid rounded-full size-12 place-items-center bg-n-alpha-2">
          <span class="i-ri-sparkling-2-line size-6 text-n-slate-10" />
        </span>
        <span class="text-base font-semibold text-n-slate-12">
          {{ t('AI_MARKUP.ERROR.TITLE') }}
        </span>
        <span class="max-w-md text-sm text-n-slate-11">
          {{ t('AI_MARKUP.ERROR.DESCRIPTION') }}
        </span>
        <Button
          :label="t('AI_MARKUP.ERROR.RETRY')"
          icon="i-lucide-refresh-cw"
          size="sm"
          variant="faded"
          @click="load"
        />
      </div>
      <div v-else class="flex flex-col gap-5 mt-2">
        <SectionCard
          v-model:enabled="config.enabled"
          :title="t('AI_MARKUP.GENERAL.TITLE')"
          :description="t('AI_MARKUP.GENERAL.DESCRIPTION')"
          icon="i-ri-sparkling-2-fill"
          icon-class="bg-gradient-to-br from-[#7C3AED] to-[#E30611] text-white"
          toggleable
        >
          <div class="grid gap-4 sm:grid-cols-[1fr_1fr_8rem]">
            <Input
              v-model="config.provider.model"
              :label="t('AI_MARKUP.GENERAL.MODEL')"
              placeholder="rocketgpt-4o-mini"
              custom-input-class="font-mono"
            />
            <Input
              v-model="config.provider.base_url"
              :label="t('AI_MARKUP.GENERAL.ENDPOINT')"
              placeholder="https://"
              custom-input-class="font-mono"
            />
            <Input
              v-model="temperature"
              :label="t('AI_MARKUP.GENERAL.TEMPERATURE')"
              custom-input-class="font-mono"
            />
          </div>
          <p
            class="flex items-center gap-1.5 mt-3 mb-0 text-xs text-n-slate-10"
          >
            <span class="i-lucide-info size-3.5" />
            {{ t('AI_MARKUP.GENERAL.HINT') }}
          </p>
        </SectionCard>

        <SectionCard
          v-model:enabled="config.sentiment.enabled"
          :title="t('AI_MARKUP.SENTIMENT.TITLE')"
          :description="t('AI_MARKUP.SENTIMENT.DESCRIPTION')"
          icon="i-lucide-smile"
          icon-class="bg-n-teal-3 text-n-teal-11"
          toggleable
        >
          <div class="flex flex-col gap-4">
            <div class="flex flex-wrap items-end gap-6">
              <div class="flex flex-col gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('AI_MARKUP.SENTIMENT.SCOPE') }}
                </span>
                <Select
                  v-model="config.sentiment.scope"
                  :options="scopeOptions"
                />
              </div>
              <label
                class="flex items-center gap-2 pb-2 text-sm text-n-slate-12"
              >
                <Switch v-model="config.sentiment.create_labels" />
                {{ t('AI_MARKUP.SENTIMENT.CREATE_LABELS') }}
              </label>
            </div>
            <div class="flex gap-2">
              <span
                v-for="key in ['POSITIVE', 'NEUTRAL', 'NEGATIVE']"
                :key="key"
                class="inline-flex items-center gap-1 h-6 px-2 rounded-md text-xs font-medium"
                :class="{
                  'bg-n-teal-3 text-n-teal-11': key === 'POSITIVE',
                  'bg-n-alpha-2 text-n-slate-11': key === 'NEUTRAL',
                  'bg-n-ruby-3 text-n-ruby-11': key === 'NEGATIVE',
                }"
              >
                {{ t(`MULTICHAT.SENTIMENT.${key}`) }}
              </span>
              <span class="self-center text-xs text-n-slate-10">
                {{ t('AI_MARKUP.SENTIMENT.ATTRIBUTE_HINT') }}
              </span>
            </div>
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('AI_MARKUP.SENTIMENT.INSTRUCTIONS') }}
              </span>
              <textarea
                v-model="config.sentiment.instructions"
                rows="4"
                class="w-full !mb-0 px-3 py-2 text-sm leading-5 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 resize-y focus:outline-none focus:ring-1 focus:ring-n-brand"
                :placeholder="t('AI_MARKUP.SENTIMENT.INSTRUCTIONS_PLACEHOLDER')"
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          v-model:enabled="config.autotags.enabled"
          :title="t('AI_MARKUP.AUTOTAGS.TITLE')"
          :description="t('AI_MARKUP.AUTOTAGS.DESCRIPTION_TEXT')"
          icon="i-lucide-tags"
          icon-class="bg-n-blue-3 text-n-blue-11"
          toggleable
        >
          <template #actions>
            <div class="flex items-center gap-2 mt-1 text-xs text-n-slate-11">
              {{ t('AI_MARKUP.AUTOTAGS.MAX_TAGS') }}
              <Select
                v-model="config.autotags.max_tags"
                :options="maxTagsOptions"
                class="[&_select]:!py-1 [&_select]:!text-xs"
              />
            </div>
          </template>
          <AutotagsTable v-model="config.autotags.items" />
        </SectionCard>

        <SectionCard
          v-model:enabled="config.spam.enabled"
          :title="t('AI_MARKUP.SPAM.TITLE')"
          :description="t('AI_MARKUP.SPAM.DESCRIPTION')"
          icon="i-lucide-shield-alert"
          icon-class="bg-n-amber-3 text-n-amber-11"
          toggleable
        >
          <SpamRules v-model="config.spam.rules" />
        </SectionCard>

        <SectionCard
          :title="t('AI_MARKUP.TEST.TITLE')"
          :description="t('AI_MARKUP.TEST.DESCRIPTION')"
          icon="i-lucide-flask-conical"
          icon-class="bg-n-iris-3 text-n-iris-11"
        >
          <TestPanel :tags="config.autotags.items" />
        </SectionCard>

        <SectionCard
          :title="t('AI_MARKUP.STATS.TITLE', { days: stats.period_days || 30 })"
          :description="t('AI_MARKUP.STATS.DESCRIPTION')"
          icon="i-lucide-chart-no-axes-column"
          icon-class="bg-n-slate-3 text-n-slate-11"
        >
          <StatsPanel :stats="stats" :tags="config.autotags.items" />
        </SectionCard>
      </div>
    </template>
  </SettingsLayout>
</template>
